import { useCallback, useEffect, useMemo, useState } from "react";

import ROLES, { ROLES_LIST } from "../../../constants/roles";

import writeXlsxFile, { Row, SheetData } from "write-excel-file";

import moment from "moment";
import _ from "lodash";

import {
  Grid,
  Typography,
  Stack,
  IconButton,
  Avatar,
  Autocomplete,
  Popover,
  TextField,
  Badge,
  Tooltip,
  Box,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { ChairTypes } from "../../../constants/chairTypes";
import { IUserBaseResponse } from "../../../types/user";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LoadingButton } from "@mui/lab";
import { ClinicResponse } from "../../../types/clinic";
import { IAbsenceTypeResponse } from "../../../types/classifiers";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleFullScreenButton,
  MRT_Row,
  MRT_ShowHideColumnsButton,
  MRT_SortingState,
  MRT_ToggleDensePaddingButton,
} from "material-react-table";

const TimesheetsReports: React.FunctionComponent<{}> = () => {
  const [isExporting, setIsExporting] = useState(false);

  const [absenceTypes, setAbsenceTypes] = useState<IAbsenceTypeResponse[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState<Date>(moment().toDate());

  const [filterValues, setFilterValues] = useState<IFilterValues>({});
  const [filtersAnchorEl, setFiltersAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFiltersAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFiltersAnchorEl(null);
  };

  const handleFilterChange = useCallback((values: IFilterValues) => {
    setFilterValues(values);
  }, []);

  // TODO: move to types and reuse nested types
  interface ITimesheetsReportResponse {
    _id: string;
    user: {
      _id: string;
      name: string;
      givenName: string;
      familyName: string;
      image: string;
    };
    timesheets: {
      _id: string;
      startedAt: Date;
      finishedAt: Date;
      schedule: {
        _id: string;
        type: ChairTypes;
      };
    }[];
    absences: {
      _id: string;
      from: Date;
      to: Date;
      status: string;
      type: {
        _id: string;
        name: string;
        abbreviation: string;
      };
    }[];
  }

  const [data, setData] = useState<ITimesheetsReportResponse[]>([]);

  const columns = useMemo<MRT_ColumnDef<ITimesheetsReportResponse>[]>(() => {
    let monthDayColumns: MRT_ColumnDef<ITimesheetsReportResponse>[] = [];
    const daysInMonth = moment(date).daysInMonth();
    for (let i = 1; i <= daysInMonth; i++) {
      monthDayColumns.push({
        id: `${i}.${moment(date).month() + 1}`,
        accessorKey: `day${i}`,
        header: i.toString(),
        maxSize: 25,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        enableSorting: false,
        accessorFn: (params) => {
          const timesheet = params.timesheets.filter((timesheet) => {
            return moment(timesheet.startedAt).date() === i;
          });

          if (!timesheet) {
            return null;
          }

          const absence = params.absences.find((absence) => {
            return (
              moment(absence.from).date() <= i && moment(absence.to).date() >= i
            );
          });

          if (absence) {
            return (
              <Tooltip title={absence.type.name}>
                <Typography>{absence.type.abbreviation}</Typography>
              </Tooltip>
            );
          }

          let duration = moment.duration(0);

          timesheet.forEach((timesheet) => {
            duration.add(
              moment.duration(
                moment(timesheet.finishedAt).diff(moment(timesheet.startedAt)),
              ),
            );
          });

          let isSurgeon = false;
          timesheet.forEach((timesheet) => {
            if (
              timesheet.schedule &&
              timesheet.schedule.type === ChairTypes.Surgeon
            ) {
              isSurgeon = true;
            }
          });

          return (
            <Typography color={isSurgeon ? "error" : "default"}>
              {_.round(duration.asHours(), 2)}
            </Typography>
          );
        },
      });
    }

    let absenceTypeColumns: MRT_ColumnDef<ITimesheetsReportResponse>[] = [];
    absenceTypes.forEach((absenceType) => {
      absenceTypeColumns.push({
        id: `${absenceType._id}.${moment(date).month() + 1}`,
        accessorKey: absenceType._id,
        header: absenceType.abbreviation,
        Header(props) {
          return (
            <Tooltip title={absenceType.name}>
              <Typography>{absenceType.abbreviation}</Typography>
            </Tooltip>
          );
        },
        enableSorting: false,
        maxSize: 40,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        accessorFn: (params) => {
          const absences = params.absences.filter((absence) => {
            return absence.type._id === absenceType._id;
          });

          let totalDays = 0;

          absences.forEach((absence) => {
            totalDays +=
              moment(absence.to).diff(moment(absence.from), "days") + 1;
          });

          return totalDays;
        },
      });
    });

    return [
      {
        accessorKey: "image",
        header: "",
        accessorFn: (params) => {
          if (!params.user || !params.user.image) {
            return null;
          }
          return <Avatar alt={params.user.name} src={params.user.image} />;
        },
        maxSize: 60,
        minSize: 60,
      },
      {
        accessorKey: "familyName",
        header: "Uzvārds",
        accessorFn: (params) => {
          if (!params.user) {
            return null;
          }

          return params.user.familyName;
        },
        minSize: 70,
        maxSize: 120,
        enableSorting: true,
      },
      {
        accessorKey: "givenName",
        header: "Vārds",
        accessorFn: (params) => {
          if (!params.user) {
            return null;
          }

          return params.user.givenName;
        },
        minSize: 70,
        maxSize: 120,
        enableSorting: true,
      },
      ...monthDayColumns,
      {
        id: `totalHours.${moment(date).month() + 1}`,
        accessorKey: "totalHours",
        header: "Stundas kopā",
        enableSorting: false,
        maxSize: 110,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        accessorFn: (params) => {
          let duration = moment.duration(0);
          let total = 0;

          params.timesheets.forEach((timesheet) => {
            const dur = moment.duration(
              moment(timesheet.finishedAt).diff(moment(timesheet.startedAt)),
            );
            duration.add(dur);

            total += dur.asHours();

            console.log(
              "Date:",
              timesheet.startedAt,
              "Duration:",
              dur.asHours(),
            );

            // duration.add(
            //   moment.duration(
            //     moment(timesheet.finishedAt).diff(moment(timesheet.startedAt)),
            //   ),
            // );
          });

          // return _.round(duration.asHours(), 2);
          return _.round(total, 2);
          // const totalHours = params.timesheets.reduce(
          //   (totalHours, timesheet) => {
          //     const duration = moment.duration(
          //       moment(timesheet.finishedAt).diff(moment(timesheet.startedAt)),
          //     );

          //     return totalHours + duration.asHours();
          //   },
          //   0,
          // );

          // return _.round(totalHours, 2);
        },
      },
      {
        id: `totalDays.${moment(date).month() + 1}`,
        accessorKey: "totalDays",
        header: "Dienu skaits",
        enableSorting: false,
        maxSize: 100,
        accessorFn: (params) => {
          return params.timesheets.length;
        },
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      ...absenceTypeColumns,
    ];
  }, [date, absenceTypes]);

  useEffect(() => {
    axios
      .get<IAbsenceTypeResponse[]>(`/classifiers/absenceTypes`)
      .then((response) => {
        setAbsenceTypes(response.data);
      })
      .catch((error) => {
        console.log("error:", error);
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    axios
      .get<ITimesheetsReportResponse[]>("/timesheets", {
        params: {
          date: date,
          filters: {
            user: filterValues.user?._id,
            clinic: filterValues.clinic?._id,
            role: filterValues.role,
          },
        },
      })
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [date, filterValues]);

  const exportXlsx = useCallback(
    async (rows: MRT_Row<ITimesheetsReportResponse>[]) => {
      setIsExporting(true);

      const daysInMonth = moment(date).daysInMonth();
      const totalColumns = daysInMonth + 3 + absenceTypes.length;
      const defaultFontSize = 12;

      let sheetData: SheetData = [];

      let companyName: Row = [
        {
          value: 'SIA "ĢIMENES ZOBĀRSTNIECĪBA"',
          fontWeight: "bold",
          span: totalColumns,
        },
        ...new Array(totalColumns).fill(null),
      ];
      sheetData.push(companyName);

      let companyAddress: Row = [
        {
          value: "Valmieras iela 9, Cēsis, Cēsu novads, LV-4101",
          fontWeight: "bold",
          span: totalColumns,
        },
        ...new Array(totalColumns).fill(null),
      ];
      sheetData.push(companyAddress);

      let companyRegistrationNumber: Row = [
        {
          value: "Reģ. Nr. 40003289189",
          fontWeight: "bold",
          span: totalColumns,
        },
        ...new Array(totalColumns).fill(null),
      ];
      sheetData.push(companyRegistrationNumber);

      if (filterValues.clinic) {
        let department: Row = [
          {
            value: `struktūrvienība - ${filterValues.clinic.name}`,
            fontWeight: "bold",
            span: totalColumns,
          },
          ...new Array(totalColumns).fill(null),
        ];

        sheetData.push(department);
      }

      let abbreviations: Row = [
        {
          value: absenceTypes
            .map((absenceType) => {
              return `${absenceType.abbreviation}: ${absenceType.name}`;
            })
            .join(", "),
          span: totalColumns,
          align: "right",
        },
        ...new Array(totalColumns).fill(null),
      ];
      sheetData.push(abbreviations);

      sheetData.push([]);

      let firstRow: Row = [
        {
          value: `Darba laika uzskaite par: ${moment(date)
            .startOf("month")
            .format("L")}-${moment(date).endOf("month").format("L")}${
            filterValues.user ? ` - ${filterValues.user.name}` : ""
          }`,
          fontWeight: "bold",
          span: totalColumns,
          align: "center",
        },
        ...new Array(totalColumns).fill(null),
      ];
      sheetData.push(firstRow);
      sheetData.push([]);

      let tableHeaders: Row = [
        { value: "Darbinieks", fontWeight: "bold", borderStyle: "thin" },
      ];
      let columns = [{ width: 20 }];

      for (let i = 1; i <= daysInMonth; i++) {
        tableHeaders.push({
          value: i,
          fontWeight: "bold",
          align: "center",
          borderStyle: "thin",
        });
        columns.push({ width: 5 });
      }

      tableHeaders.push({
        value: "Stundas kopā",
        fontWeight: "bold",
        align: "center",
        borderStyle: "thin",
      });
      columns.push({ width: 15 });

      tableHeaders.push({
        value: "Dienu skaits",
        fontWeight: "bold",
        align: "center",
        borderStyle: "thin",
      });
      columns.push({ width: 15 });

      absenceTypes.forEach((absenceType) => {
        tableHeaders.push({
          value: absenceType.abbreviation,
          fontWeight: "bold",
          align: "center",
          borderStyle: "thin",
        });
        columns.push({ width: 5 });
      });

      sheetData.push(tableHeaders);

      rows.forEach((row) => {
        let rowData: Row = [
          {
            value:
              row.original.user.familyName + " " + row.original.user.givenName,
            fontSize: defaultFontSize,
            borderStyle: "thin",
          },
        ];

        let totalHours = 0;

        for (let i = 1; i <= daysInMonth; i++) {
          const timesheet = row.original.timesheets.filter((timesheet) => {
            return moment(timesheet.startedAt).date() === i;
          });

          const absence = row.original.absences.find((absence) => {
            return (
              moment(absence.from).date() <= i && moment(absence.to).date() >= i
            );
          });

          if (absence) {
            rowData.push({
              value: absence.type.abbreviation,
              fontSize: defaultFontSize,
              align: "center",
              borderStyle: "thin",
            });
            continue;
          }

          if (!timesheet) {
            rowData.push({
              value: "",
              fontSize: defaultFontSize,
              type: Number,
              borderStyle: "thin",
            });
            continue;
          }

          let duration = moment.duration(0);

          timesheet.map((timesheet) => {
            duration.add(
              moment.duration(
                moment(timesheet.finishedAt).diff(moment(timesheet.startedAt)),
              ),
            );
          });

          totalHours += duration.asHours();

          let isSurgeon = false;

          timesheet.map((timesheet) => {
            if (
              timesheet.schedule &&
              timesheet.schedule.type === ChairTypes.Surgeon
            ) {
              isSurgeon = true;
            }
          });

          rowData.push({
            value: _.round(duration.asHours(), 2),
            fontSize: defaultFontSize,
            align: "center",
            backgroundColor: isSurgeon ? "#FFD966" : undefined,
            borderStyle: "thin",
          });
        }

        rowData.push({
          value: _.round(totalHours, 2),
          fontSize: defaultFontSize,
          backgroundColor: "#e6ecff",
          align: "center",
          borderStyle: "thin",
        });

        rowData.push({
          value: row.original.timesheets.length,
          fontSize: defaultFontSize,
          backgroundColor: "#e6ecff",
          borderStyle: "thin",
        });

        absenceTypes.forEach((absenceType) => {
          const absences = row.original.absences.filter((absence) => {
            return absence.type._id === absenceType._id;
          });

          let totalDays = 0;

          absences.forEach((absence) => {
            totalDays +=
              moment(absence.to).diff(moment(absence.from), "days") + 1;
          });

          rowData.push({
            value: totalDays,
            fontSize: defaultFontSize,
            align: "center",
            backgroundColor: "#e6ecff",
            borderStyle: "thin",
          });
        });

        sheetData.push(rowData);
      });

      const fileName = `${
        filterValues.clinic ? `${filterValues.clinic.name}_` : ""
      }${
        filterValues.user ? `${filterValues.user.name}_` : ""
      }timesheet_${moment(date).startOf("month").format("L")}-${moment(date)
        .endOf("month")
        .format("L")}`.slice(0, -1);

      await writeXlsxFile(sheetData, {
        columns,
        fileName: fileName + ".xlsx",
        sheet: "timesheet",
      });

      setIsExporting(false);
    },
    [date, filterValues, absenceTypes],
  );

  return (
    <Stack spacing={2}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4">Noziņotās stundas</Typography>
        </Grid>
      </Grid>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton
            onClick={() =>
              setDate((currentDate) =>
                moment(currentDate).subtract(1, "month").toDate(),
              )
            }
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography>
            {moment(date).startOf("month").format("LL")} -{" "}
            {moment(date).endOf("month").format("LL")}
          </Typography>
          <IconButton
            onClick={() =>
              setDate((currentDate) =>
                moment(currentDate).add(1, "month").toDate(),
              )
            }
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      <MaterialReactTable
        columns={columns}
        enableColumnActions={false}
        data={data || []}
        getRowId={(row) => row._id}
        enableTableFooter={false}
        enableGlobalFilter={false}
        enableBottomToolbar={false}
        initialState={{
          columnPinning: {
            left: ["image", "familyName", "givenName"],
          },
          sorting: [
            {
              id: "familyName",
              desc: false,
            },
            {
              id: "givenName",
              desc: false,
            },
          ],
        }}
        state={{
          isLoading,
          pagination: {
            pageIndex: 0,
            pageSize: data.length || 10,
          },
        }}
        renderTopToolbarCustomActions={({ table }) => (
          <>
            <LoadingButton
              loading={isExporting}
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                exportXlsx(table.getRowModel().rows);
              }}
            >
              Eksportēt XLSX
            </LoadingButton>
          </>
        )}
        renderToolbarInternalActions={({ table }) => (
          <>
            <IconButton onClick={handleFilterClick}>
              <Badge
                color="secondary"
                badgeContent={
                  Object.values(filterValues).filter((item) => item).length
                }
              >
                <FilterAltIcon />
              </Badge>
            </IconButton>

            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />
            <MRT_ToggleFullScreenButton table={table} />
          </>
        )}
      />

      <Popover
        open={Boolean(filtersAnchorEl)}
        anchorEl={filtersAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
      >
        <Filters values={filterValues} onFilterChange={handleFilterChange} />
      </Popover>
    </Stack>
  );
};

interface IFilterValues {
  user?: IUserBaseResponse;
  clinic?: ClinicResponse;
  role?: ROLES;
}

interface IFIlterProps {
  values: IFilterValues;
  onFilterChange: (values: IFilterValues) => void;
}

const Filters: React.FunctionComponent<IFIlterProps> = ({
  values,
  onFilterChange,
}) => {
  const [users, setUsers] = useState<IUserBaseResponse[]>([]);
  const [clinics, setClinics] = useState<ClinicResponse[]>([]);

  useEffect(() => {
    axios
      .get<IUserBaseResponse[]>(`/users/list`)
      .then((response) => {
        setUsers(response.data);
      })
      .catch((error) => {
        console.log("error:", error);
      });

    axios
      .get<ClinicResponse[]>("/clinics")
      .then((res) => {
        setClinics(res.data);
      })
      .catch((err) => {
        console.log("err:", err);
      });
  }, []);

  useEffect(() => {
    onFilterChange(values);
  }, [values]);

  return (
    <Stack spacing={2} sx={{ padding: 2, width: 400, maxWidth: "100%" }}>
      <Typography variant="h6">Filtri</Typography>
      <Autocomplete
        options={Object.keys(ROLES_LIST) as ROLES[]}
        value={values.role}
        getOptionLabel={(option) => ROLES_LIST[option].name}
        renderInput={(params) => <TextField {...params} label="Loma" />}
        onChange={(e, value) => {
          onFilterChange({
            ...values,
            role: value ? value : undefined,
          });
        }}
      />
      <Autocomplete
        options={users}
        value={values.user}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => <TextField {...params} label="Darbinieks" />}
        renderOption={(props, option) => {
          return (
            <Stack
              {...props}
              component="li"
              direction="row"
              spacing={1}
              alignItems="center"
              key={option._id}
            >
              <Avatar
                alt={option.name}
                src={option.image}
                sx={{ width: 24, height: 24 }}
              />
              <Typography>{option.name}</Typography>
            </Stack>
          );
        }}
        onChange={(e, value) => {
          onFilterChange({
            ...values,
            user: value ? value : undefined,
          });
        }}
      />
      <Autocomplete
        options={clinics}
        value={values.clinic}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => <TextField {...params} label="Klīnika" />}
        onChange={(e, value) => {
          onFilterChange({
            ...values,
            clinic: value ? value : undefined,
          });
        }}
      />
    </Stack>
  );
};

export default TimesheetsReports;
